import { isSameDay } from 'date-fns';
import { isBetween } from 'shared/lib/date-fns';
import { HabitSchedule, ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { v4 as uuidv4 } from 'uuid';

import { getOccurrences } from './get-occurrences';
import { getSchedulePlanning } from './get-schedule-due-date';
import { getScheduleFrequencyTimeframeByDate } from './get-schedule-timeframe-by-date';

export type Options = {
  id?: ID;
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const deletePlannedOccurrenceForDate = (
  schedule: HabitSchedule,
  date: Timestamp,
  { id, weekStartsOn, today }: Options,
): HabitSchedule => {
  const plannedOccurrences = schedule.plannedOccurrences?.length
    ? structuredClone(schedule.plannedOccurrences)
    : [];

  // get the timeframe that belongs to the date to add skips when deleting the last planned occurrence in the timeframe
  const timeframe = getScheduleFrequencyTimeframeByDate(
    schedule,
    date,
    weekStartsOn,
  );

  // if we have an id, it could mean we have to delete the occurrence with that specific id
  const occurrenceToDelete = plannedOccurrences.findIndex(
    (plannedOccurrence) =>
      plannedOccurrence.id === id || isSameDay(plannedOccurrence.date, date),
  );

  if (occurrenceToDelete >= 0) {
    const timeframeOccurrences =
      timeframe && plannedOccurrences.length
        ? plannedOccurrences.filter(({ date }) =>
            isBetween(date, timeframe.startDate, timeframe.endDate),
          )
        : [];

    if (timeframeOccurrences.length === 1) {
      // if there is only one plannedOccurrence left, we are reverting to the default schedule (RRULE).
      // However, we want to ensure that the default schedule is also skipped if it wasn't explicit.
      // So we effectively want to "Clear" the timeframe.
      // To do this, we must ensure all underlying RRULE occurrences for this timeframe are SKIPPED.

      const rruleOccurrences = getOccurrences([schedule], timeframe);
      const newSkips = rruleOccurrences.filter(
        (rruleDate) =>
          !schedule.skips?.some((skip) => isSameDay(skip, rruleDate)),
      );

      const newSkipEntries = newSkips.map((skipDate) => ({
        date: skipDate,
        id: uuidv4(),
        state: ScheduleEntryState.Skip,
        hidden: true,
        value: null,
      }));

      const entries = [
        ...schedule.entries.filter((entry) => !isSameDay(entry.date, date)),
        {
          date,
          id: uuidv4(),
          state: ScheduleEntryState.Skip,
          hidden: true,
          value: null,
        },
        ...newSkipEntries,
      ];
      const completions = schedule.completions?.filter(
        (completion) => !isSameDay(completion, date),
      );
      const skips = [
        ...(schedule.skips?.filter((skip) => !isSameDay(skip, date)) ?? []),
        date,
        ...newSkips,
      ];

      // We must removed the planned occurrence, otherwise it remains visible as a manual occurrence
      plannedOccurrences.splice(occurrenceToDelete, 1);

      return {
        ...schedule,
        entries,
        completions,
        skips,
        plannedOccurrences,
      };
    }
    plannedOccurrences.splice(occurrenceToDelete, 1);
    return {
      ...schedule,
      plannedOccurrences,
    };
  }

  // if no occurrence was found, we have to create occurrences for the timeframe and remove the one for the specific date
  const timeframePlannedOccurrences = getSchedulePlanning(schedule, {
    ...timeframe,
    today,
    weekStartsOn,
  });
  if (timeframePlannedOccurrences.length === 0) {
    // No planned occurrences exist for this timeframe (e.g. a future timeframe
    // where `getSchedulePlanning` doesn't generate "remaining" occurrences
    // because today isn't in it). Treat this as fully skipping the timeframe
    // by adding a skip entry for the date, so the frequency row disappears.
    const entries = [
      ...schedule.entries.filter((entry) => !isSameDay(entry.date, date)),
      {
        date,
        id: uuidv4(),
        state: ScheduleEntryState.Skip,
        hidden: true,
        value: null,
      },
    ];
    const completions = schedule.completions?.filter(
      (completion) => !isSameDay(completion, date),
    );
    const skips = [
      ...(schedule.skips?.filter((skip) => !isSameDay(skip, date)) ?? []),
      date,
    ];

    return {
      ...schedule,
      entries,
      completions,
      skips,
      plannedOccurrences,
    };
  }
  if (timeframePlannedOccurrences.length === 1) {
    // if there is only one plannedOccurrence in the new timeframePlannedOccurrences, add a skip for it instead of deleting it
    const entries = [
      ...schedule.entries.filter((entry) => !isSameDay(entry.date, date)),
      {
        date,
        id: uuidv4(),
        state: ScheduleEntryState.Skip,
        hidden: true,
        value: null,
      },
    ];
    const completions = schedule.completions?.filter(
      (completion) => !isSameDay(completion, date),
    );
    const skips = [
      ...(schedule.skips?.filter((skip) => !isSameDay(skip, date)) ?? []),
      date,
    ];

    return {
      ...schedule,
      entries,
      completions,
      skips,
      plannedOccurrences: [
        ...plannedOccurrences,
        ...timeframePlannedOccurrences,
      ],
    };
  }

  const filteredTimeframePlannedOccurrences =
    timeframePlannedOccurrences.filter(
      ({ date: occurrenceDate }) => !isSameDay(occurrenceDate, date),
    );

  return {
    ...schedule,
    plannedOccurrences: [
      ...plannedOccurrences,
      ...filteredTimeframePlannedOccurrences,
    ],
  };
};
